import React, { useState, useEffect } from 'react';
import { uploadOfferingImage } from '../../api/offerings';
import './OfferingForm.css';
import AdventureDateTimePicker from '../Shared/AdventureDateTimePicker';

const CATEGORY_MAP = {
  service: ['Repair', 'Cleaning', 'Installation', 'Consultation', 'Coaching', 'Delivery', 'Maintenance', 'Other'],
  event: ['Community', 'Sports', 'Adventure', 'Workshop', 'Seminar', 'Social', 'Political', 'Other'],
  product: ['Physical', 'Digital'],
  custom: ['Fabrication', 'Build', 'Design', 'Other']
};

const CURRENCIES = ['USD'];
const CURRENCY_OPTIONS = CURRENCIES.map(cur => ({ value: cur, label: cur }));

const PLATFORMS = [
  { value: 'zoom', label: 'Zoom' },
  { value: 'google_meet', label: 'Google Meet' },
  { value: 'teams', label: 'Microsoft Teams' },
  { value: 'other', label: 'Other Platform' }
];

const CATEGORY_OPTIONS = [
  { value: 'service', label: 'Service' },
  { value: 'event', label: 'Event' },
  { value: 'product', label: 'Goods' }
];

const PRICING_MODEL_OPTIONS = [
  { value: 'flat', label: 'Flat Fee' },
  { value: 'free', label: 'Free' }
];

const RECURRENCE_OPTIONS = [
  { value: 'single', label: 'Single Event' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

const CustomDropdown = ({ value, onChange, options, placeholder, disabled, hasError }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);
  const isPlaceholder = !selectedOption;
  const displayLabel = selectedOption ? selectedOption.label : (placeholder || 'Select...');

  return (
    <div className="custom-dropdown-container" ref={dropdownRef}>
      <div 
        className={`off-form__input custom-dropdown-selected ${isOpen ? 'active' : ''} ${disabled ? 'disabled' : ''} ${hasError ? 'error' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <span style={{ color: isPlaceholder ? 'rgba(255, 255, 255, 0.35)' : '#ffffff' }}>
          {displayLabel}
        </span>
        <svg 
          width="10" 
          height="6" 
          viewBox="0 0 10 6" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg" 
          style={{ 
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', 
            transition: 'transform 0.2s ease',
            opacity: disabled ? 0.3 : 0.7,
            color: 'rgba(255, 255, 255, 0.65)'
          }}
        >
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {isOpen && (
        <div className="custom-dropdown-options">
          {options.map((opt) => (
            <div 
              key={opt.value}
              className={`custom-dropdown-option ${opt.value === value ? 'selected' : ''}`}
              onClick={() => {
                onChange({ target: { value: opt.value } });
                setIsOpen(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function OfferingForm({ initialData = null, isEdit = false, isSubmitting = false, onSubmit, onCancel }) {
  // Form State
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('service');
  const [subcategory, setSubcategory] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);

  const [pricingModel, setPricingModel] = useState('flat');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [priceDescription, setPriceDescription] = useState('');
  const [inPersonType, setInPersonType] = useState('provider');

  // Phase 5: Event fields
  const [eventDate, setEventDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [maxCapacity, setMaxCapacity] = useState('');
  const [waitlistEnabled, setWaitlistEnabled] = useState(false);
  const [recurrencePattern, setRecurrencePattern] = useState('single');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [ticketPrice, setTicketPrice] = useState('');
  const [maxTicketsPerPerson, setMaxTicketsPerPerson] = useState('4');

  const [deliveryType, setDeliveryType] = useState('in_person');
  const [addressLine1, setAddressLine1] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [country, setCountry] = useState('United States');
  const [zipcode, setZipcode] = useState('');
  const [virtualLink, setVirtualLink] = useState('');
  const [virtualPlatform, setVirtualPlatform] = useState('');

  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [galleryImageUrls, setGalleryImageUrls] = useState([]);

  const getTodayFormatted = () => new Date().toISOString().split('T')[0];

  const [availableFrom, setAvailableFrom] = useState(getTodayFormatted);
  const [availableUntil, setAvailableUntil] = useState('');
  const [availabilityNotes, setAvailabilityNotes] = useState('');

  const [status, setStatus] = useState('draft');

  // UI Status
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState('');
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isUploadingGallery, setIsUploadingGallery] = useState(false);

  // Subcategory helper options
  const subcategoryOptions = CATEGORY_MAP[category] || [];

  // Load initial data for edit mode
  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      setDescription(initialData.description || '');
      setCategory(initialData.category || 'service');
      setSubcategory(initialData.subcategory || '');
      setTags(initialData.tags || []);
      setPricingModel(initialData.pricingModel || 'flat');
      setPrice(initialData.price !== undefined ? initialData.price : '');
      setCurrency(initialData.currency || 'USD');
      setPriceDescription(initialData.priceDescription || '');
      setDeliveryType(initialData.deliveryType || 'in_person');

       if (initialData.address) {
         if (initialData.address.line1 === "Customer's Location") {
           setInPersonType('customer');
           setAddressLine1("Customer's Location");
         } else {
           setInPersonType('provider');
           setAddressLine1(initialData.address.line1 || '');
         }
         setCity(initialData.address.city || '');
         setProvince(initialData.address.province || '');
         setCountry(initialData.address.country || 'United States');
         setZipcode(initialData.address.zipcode || '');
       }

      setVirtualLink(initialData.virtualLink || '');
      setVirtualPlatform(initialData.virtualPlatform || '');
      setCoverImageUrl(initialData.coverImageUrl || '');
      setGalleryImageUrls(initialData.galleryImageUrls || []);
      setAvailableFrom(initialData.availableFrom ? initialData.availableFrom.substring(0, 10) : getTodayFormatted());
      setAvailableUntil(initialData.availableUntil ? initialData.availableUntil.substring(0, 10) : '');
      setAvailabilityNotes(initialData.availabilityNotes || '');
      setStatus(initialData.status || 'draft');

      // Phase 5: Event fields
      setEventDate(initialData.eventDate ? initialData.eventDate.substring(0, 10) : '');
      setStartTime(initialData.startTime || '');
      setEndTime(initialData.endTime || '');
      setTimezone(initialData.timezone || 'UTC');
      setMaxCapacity(initialData.maxCapacity !== undefined ? initialData.maxCapacity : '');
      setWaitlistEnabled(initialData.waitlistEnabled || false);
      setRecurrencePattern(initialData.recurrencePattern || 'single');
      setRecurrenceEndDate(initialData.recurrenceEndDate ? initialData.recurrenceEndDate.substring(0, 10) : '');
      setTicketPrice(initialData.ticketPrice !== undefined ? initialData.ticketPrice : '');
      setMaxTicketsPerPerson(initialData.maxTicketsPerPerson !== undefined ? initialData.maxTicketsPerPerson : '4');
    }
  }, [initialData]);

  // Adjust subcategory when category changes
  useEffect(() => {
    if (subcategoryOptions.length > 0 && !subcategoryOptions.includes(subcategory)) {
      setSubcategory(subcategoryOptions[0].toLowerCase());
    }
  }, [category]);

  // Handle tags input keydown
  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = tagInput.trim().replace(/,/g, '');
      if (val && !tags.includes(val) && tags.length < 10) {
        setTags([...tags, val]);
        setTagInput('');
      }
    }
  };

  const removeTag = (indexToRemove) => {
    setTags(tags.filter((_, idx) => idx !== indexToRemove));
  };

  const handleFieldChange = (field, value, setter) => {
    setter(value);
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setGlobalError('');
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (title.length > 100) newErrors.title = 'Title must be under 100 characters';
    if (description && description.length > 2000) {
      newErrors.description = 'Description must be under 2000 characters';
    }

    if (category === 'event') {
      // Event checks
      if (!eventDate) newErrors.eventDate = 'Event date is required';
      if (!startTime) newErrors.startTime = 'Start time is required';
      if (!endTime) newErrors.endTime = 'End time is required';
      if (!timezone.trim()) newErrors.timezone = 'Timezone is required';
      if (ticketPrice === '') {
        newErrors.ticketPrice = 'Ticket price is required';
      } else if (isNaN(Number(ticketPrice)) || Number(ticketPrice) < 0) {
        newErrors.ticketPrice = 'Ticket price must be a valid positive number';
      }
      if (maxCapacity === '') {
        newErrors.maxCapacity = 'Capacity is required';
      } else if (isNaN(Number(maxCapacity)) || Number(maxCapacity) <= 0) {
        newErrors.maxCapacity = 'Capacity must be a valid positive number';
      }
      if (recurrencePattern !== 'single' && !recurrenceEndDate) {
        newErrors.recurrenceEndDate = 'Recurrence end date is required';
      }
    } else {
      // Pricing checks
      const needsPrice = !['free', 'cod'].includes(pricingModel);
      if (needsPrice) {
        if (price === '' || price === undefined) {
          newErrors.price = 'Price is required for paid offerings';
        } else if (isNaN(Number(price)) || Number(price) < 0) {
          newErrors.price = 'Price must be a valid positive number';
        }
      }
    }

    // Location checks
    if (category !== 'product') {
      if (!addressLine1.trim()) newErrors.addressLine1 = 'Address Line 1 is required for in-person delivery';
      if (!city.trim()) newErrors.city = 'City is required for in-person delivery';
      if (inPersonType === 'provider' && !zipcode.trim()) {
        newErrors.zipcode = 'Zip / Postal Code is required for provider location services';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Upload Cover Image
  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert("Cover image must be under 5MB");
      return;
    }

    setIsUploadingCover(true);
    try {
      const res = await uploadOfferingImage(file);
      setCoverImageUrl(res.url);
    } catch (err) {
      console.error("Cover image upload failed", err);
      alert("Image upload failed. Try again.");
    } finally {
      setIsUploadingCover(false);
    }
  };

  // Upload Gallery Images
  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const remainingCount = 5 - galleryImageUrls.length;
    const filesToUpload = files.slice(0, remainingCount);

    if (filesToUpload.length < files.length) {
      alert(`You can only upload up to 5 gallery images in total. Uploading the first ${filesToUpload.length} files.`);
    }

    setIsUploadingGallery(true);
    try {
      const uploadPromises = filesToUpload.map(file => uploadOfferingImage(file));
      const results = await Promise.all(uploadPromises);
      const urls = results.map(r => r.url);
      setGalleryImageUrls([...galleryImageUrls, ...urls]);
    } catch (err) {
      console.error("Gallery image upload failed", err);
      alert("Some gallery images failed to upload.");
    } finally {
      setIsUploadingGallery(false);
    }
  };

  const removeGalleryImage = (idx) => {
    setGalleryImageUrls(galleryImageUrls.filter((_, i) => i !== idx));
  };

  // Form Submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setGlobalError('');

    if (!validateForm()) {
      setGlobalError('Please fix the errors in the form before submitting.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const payload = {
      title: title.trim(),
      description: description.trim(),
      category,
      subcategory: subcategory.toLowerCase(),
      tags,
      pricingModel: category === 'product' ? 'flat' : (category === 'event' ? (parseFloat(ticketPrice) === 0 ? 'free' : 'flat') : pricingModel),
      price: category === 'product' ? parseFloat(price) : (category === 'event' ? (parseFloat(ticketPrice) || 0) : (['free', 'cod'].includes(pricingModel) ? null : parseFloat(price))),
      currency,
      priceDescription: category === 'product' ? 'per unit' : (category === 'event' ? 'per ticket' : priceDescription.trim()),
      deliveryType: category === 'product' ? (subcategory?.toLowerCase() === 'digital' ? 'virtual' : 'in_person') : 'in_person',
      address: category === 'product' ? null : {
        line1: addressLine1.trim(),
        city: city.trim(),
        province: province.trim(),
        country,
        zipcode: zipcode.trim()
      },
      virtualLink: null,
      virtualPlatform: null,
      coverImageUrl: coverImageUrl,
      galleryImageUrls: galleryImageUrls,
      availableFrom: availableFrom ? new Date(availableFrom).toISOString() : null,
      availableUntil: availableUntil ? new Date(availableUntil).toISOString() : null,
      availabilityNotes: availabilityNotes.trim(),
      status,
      // Phase 5 event fields
      eventDate: category === 'event' && eventDate ? new Date(eventDate).toISOString() : null,
      startTime: category === 'event' ? startTime : null,
      endTime: category === 'event' ? endTime : null,
      timezone: category === 'event' ? timezone : null,
      maxCapacity: category === 'event' && maxCapacity ? parseInt(maxCapacity, 10) : null,
      waitlistEnabled: category === 'event' ? waitlistEnabled : false,
      recurrencePattern: category === 'event' ? recurrencePattern : null,
      recurrenceEndDate: category === 'event' && recurrenceEndDate ? new Date(recurrenceEndDate).toISOString() : null,
      ticketPrice: category === 'event' && ticketPrice ? parseFloat(ticketPrice) : null,
      maxTicketsPerPerson: category === 'event' && maxTicketsPerPerson ? parseInt(maxTicketsPerPerson, 10) : null
    };

    onSubmit(payload);
  };

  return (
    <form className="off-form" onSubmit={handleSubmit}>
      {globalError && <div className="off-form__banner off-form__banner--error">{globalError}</div>}

      {/* SECTION 1: BASIC INFO */}
      <div className="off-form__section">
        <h2 className="off-form__section-title">Section 1 — Basic Info</h2>

        <div className="off-form__group">
          <label className="off-form__label">Title <span className="off-form__required">*</span></label>
          <input
            type="text"
            className={`off-form__input ${errors.title ? 'off-form__input--error' : ''}`}
            placeholder="e.g. Mountain Music Festival, Jet Ski Repairs"
            value={title}
            onChange={(e) => handleFieldChange('title', e.target.value, setTitle)}
            maxLength={100}
          />
          {errors.title && <span className="off-form__error-text">{errors.title}</span>}
          <span className="off-form__hint">{title.length}/100 characters</span>
        </div>

        <div className="off-form__group">
          <label className="off-form__label">Description</label>
          <textarea
            className={`off-form__input off-form__input--textarea ${errors.description ? 'off-form__input--error' : ''}`}
            placeholder="Provide a detailed description of your offering..."
            value={description}
            onChange={(e) => handleFieldChange('description', e.target.value, setDescription)}
            maxLength={2000}
          />
          {errors.description && <span className="off-form__error-text">{errors.description}</span>}
          <span className="off-form__hint">{description.length}/2000 characters</span>
        </div>

        <div className="off-form__row">
          <div className="off-form__group off-form__group--half">
            <label className="off-form__label">Category <span className="off-form__required">*</span></label>
            <CustomDropdown
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              options={CATEGORY_OPTIONS}
            />
          </div>

          <div className="off-form__group off-form__group--half">
            <label className="off-form__label">Subcategory</label>
            <CustomDropdown
              value={subcategory}
              onChange={(e) => setSubcategory(e.target.value)}
              options={subcategoryOptions.map((opt) => ({
                value: opt.toLowerCase(),
                label: opt
              }))}
              placeholder="Select Subcategory"
            />
          </div>
        </div>

        <div className="off-form__group">
          <label className="off-form__label">Tags</label>
          <div className="off-form__tags-container">
            {tags.map((tag, index) => (
              <span key={index} className="off-form__tag">
                {tag}
                <button type="button" className="off-form__tag-remove" onClick={() => removeTag(index)}>&times;</button>
              </span>
            ))}
            <input
              type="text"
              className="off-form__tags-input"
              placeholder={tags.length < 10 ? "Add tag and press Enter/comma" : "Max 10 tags reached"}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagInputKeyDown}
              disabled={tags.length >= 10}
            />
          </div>
          <span className="off-form__hint">Press Enter or Comma after typing each tag. Max 10 tags.</span>
        </div>
      </div>

      {/* SECTION 2: PRICING / EVENT DETAILS */}
      <div className="off-form__section">
        {category === 'event' ? (
          <>
            <h2 className="off-form__section-title">Section 2 — Event Details & Ticketing</h2>
            
            <div className="off-form__row">
              <div className="off-form__group off-form__group--third">
                <label className="off-form__label">Event Date <span className="off-form__required">*</span></label>
                <AdventureDateTimePicker
                  type="date"
                  label="Event Date *"
                  value={eventDate}
                  onChange={(val) => handleFieldChange('eventDate', val, setEventDate)}
                  minDate={getTodayFormatted()}
                />
                {errors.eventDate && <span className="off-form__error-text">{errors.eventDate}</span>}
              </div>

              <div className="off-form__group off-form__group--third">
                <label className="off-form__label">Start Time <span className="off-form__required">*</span></label>
                <AdventureDateTimePicker
                  type="time"
                  label="Start Time *"
                  value={startTime}
                  onChange={(val) => handleFieldChange('startTime', val, setStartTime)}
                />
                {errors.startTime && <span className="off-form__error-text">{errors.startTime}</span>}
              </div>

              <div className="off-form__group off-form__group--third">
                <label className="off-form__label">End Time <span className="off-form__required">*</span></label>
                <AdventureDateTimePicker
                  type="time"
                  label="End Time *"
                  value={endTime}
                  onChange={(val) => handleFieldChange('endTime', val, setEndTime)}
                />
                {errors.endTime && <span className="off-form__error-text">{errors.endTime}</span>}
              </div>
            </div>

            <div className="off-form__row">
              <div className="off-form__group off-form__group--half">
                <label className="off-form__label">Timezone <span className="off-form__required">*</span></label>
                <input
                  type="text"
                  className={`off-form__input ${errors.timezone ? 'off-form__input--error' : ''}`}
                  placeholder="e.g. EST, MST, UTC"
                  value={timezone}
                  onChange={(e) => handleFieldChange('timezone', e.target.value, setTimezone)}
                />
                {errors.timezone && <span className="off-form__error-text">{errors.timezone}</span>}
              </div>

              <div className="off-form__group off-form__group--half">
                <label className="off-form__label">Recurrence Schedule</label>
                <CustomDropdown
                  value={recurrencePattern}
                  onChange={(e) => setRecurrencePattern(e.target.value)}
                  options={RECURRENCE_OPTIONS}
                />
              </div>
            </div>

            {recurrencePattern !== 'single' && (
              <div className="off-form__group" style={{ marginBottom: '16px' }}>
                <label className="off-form__label">Recurrence End Date <span className="off-form__required">*</span></label>
                <AdventureDateTimePicker
                  type="date"
                  label="Recurrence End Date *"
                  value={recurrenceEndDate}
                  onChange={(val) => handleFieldChange('recurrenceEndDate', val, setRecurrenceEndDate)}
                  minDate={eventDate || getTodayFormatted()}
                />
                {errors.recurrenceEndDate && <span className="off-form__error-text">{errors.recurrenceEndDate}</span>}
              </div>
            )}

            <div className="off-form__row">
              <div className="off-form__group off-form__group--third">
                <label className="off-form__label">Ticket Price <span className="off-form__required">*</span></label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={`off-form__input ${errors.ticketPrice ? 'off-form__input--error' : ''}`}
                  placeholder="0.00 (or 0 for Free)"
                  value={ticketPrice}
                  onChange={(e) => handleFieldChange('ticketPrice', e.target.value, setTicketPrice)}
                />
                {errors.ticketPrice && <span className="off-form__error-text">{errors.ticketPrice}</span>}
              </div>

              <div className="off-form__group off-form__group--third">
                <label className="off-form__label">Max Capacity (Spots) <span className="off-form__required">*</span></label>
                <input
                  type="number"
                  min="1"
                  className={`off-form__input ${errors.maxCapacity ? 'off-form__input--error' : ''}`}
                  placeholder="e.g. 50"
                  value={maxCapacity}
                  onChange={(e) => handleFieldChange('maxCapacity', e.target.value, setMaxCapacity)}
                />
                {errors.maxCapacity && <span className="off-form__error-text">{errors.maxCapacity}</span>}
              </div>

              <div className="off-form__group off-form__group--third">
                <label className="off-form__label">Max Tickets Per Person</label>
                <input
                  type="number"
                  min="1"
                  className="off-form__input"
                  placeholder="Default 4"
                  value={maxTicketsPerPerson}
                  onChange={(e) => setMaxTicketsPerPerson(e.target.value)}
                />
              </div>
            </div>

            <div className="off-form__group">
              <label className="off-form__radio-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={waitlistEnabled}
                  onChange={(e) => setWaitlistEnabled(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                Enable Waitlist (Allow signups when capacity is full)
              </label>
            </div>
          </>
        ) : (
          <>
            <h2 className="off-form__section-title">Section 2 — Pricing</h2>
            
            <div className="off-form__row">
              {category !== 'product' && (
                <div className="off-form__group off-form__group--third">
                  <label className="off-form__label">Pricing Model <span className="off-form__required">*</span></label>
                  <CustomDropdown
                    value={pricingModel}
                    onChange={(e) => setPricingModel(e.target.value)}
                    options={PRICING_MODEL_OPTIONS}
                  />
                </div>
              )}

              {(category === 'product' || !['free', 'cod'].includes(pricingModel)) && (
                <>
                  <div className="off-form__group off-form__group--third">
                    <label className="off-form__label">Price <span className="off-form__required">*</span></label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={`off-form__input ${errors.price ? 'off-form__input--error' : ''}`}
                      placeholder="0.00"
                      value={price}
                      onChange={(e) => handleFieldChange('price', e.target.value, setPrice)}
                    />
                    {errors.price && <span className="off-form__error-text">{errors.price}</span>}
                  </div>

                  <div className="off-form__group off-form__group--third">
                    <label className="off-form__label">Currency <span className="off-form__required">*</span></label>
                    <CustomDropdown
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      options={CURRENCY_OPTIONS}
                    />
                  </div>
                </>
              )}
            </div>

            {category !== 'product' && (
              <div className="off-form__group">
                <label className="off-form__label">Price Description</label>
                <input
                  type="text"
                  className="off-form__input"
                  placeholder="e.g. Starting from, Per person, per group session"
                  value={priceDescription}
                  onChange={(e) => setPriceDescription(e.target.value)}
                  maxLength={100}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* SECTION 3: LOCATION */}
      {category !== 'product' && (
        <div className="off-form__section">
          <h2 className="off-form__section-title">Section 3 — Location</h2>

          <div className="off-form__nested-fields">
            <h3 className="off-form__nested-title">Address Details</h3>
            
            <div className="off-form__group" style={{ marginBottom: '16px' }}>
              <label className="off-form__label" style={{ marginBottom: '8px', display: 'block' }}>In-Person Service Location Type</label>
              <div style={{ display: 'flex', gap: '20px' }}>
                <label className="off-form__radio-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="inPersonType"
                    value="provider"
                    checked={inPersonType === 'provider'}
                    onChange={() => {
                      setInPersonType('provider');
                      if (addressLine1 === "Customer's Location") setAddressLine1('');
                    }}
                  />
                  At Provider's Address / Shop / Venue
                </label>
                <label className="off-form__radio-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="inPersonType"
                    value="customer"
                    onChange={() => {
                      setInPersonType('customer');
                      setAddressLine1("Customer's Location");
                    }}
                    checked={inPersonType === 'customer'}
                  />
                  At Customer's Address (Home Service)
                </label>
              </div>
            </div>

            {inPersonType === 'provider' ? (
              <div className="off-form__group">
                <label className="off-form__label">Address Line 1 <span className="off-form__required">*</span></label>
                <input
                  type="text"
                  className={`off-form__input ${errors.addressLine1 ? 'off-form__input--error' : ''}`}
                  placeholder="Street address, suite unit"
                  value={addressLine1 === "Customer's Location" ? "" : addressLine1}
                  onChange={(e) => handleFieldChange('addressLine1', e.target.value, setAddressLine1)}
                />
                {errors.addressLine1 && <span className="off-form__error-text">{errors.addressLine1}</span>}
              </div>
            ) : (
              <div style={{ padding: '12px', background: 'rgba(72,209,204,0.05)', borderRadius: '8px', border: '1px solid rgba(72,209,204,0.15)', marginBottom: '16px', fontSize: '13px', color: '#48d1cc' }}>
                ℹ️ Customers will be prompted to provide their address when booking this service. Please specify the general Service Area (City, State, Country) below.
              </div>
            )}

            <div className="off-form__row">
              <div className="off-form__group off-form__group--third">
                <label className="off-form__label">City <span className="off-form__required">*</span></label>
                <input
                  type="text"
                  className={`off-form__input ${errors.city ? 'off-form__input--error' : ''}`}
                  placeholder="e.g. Calgary"
                  value={city}
                  onChange={(e) => handleFieldChange('city', e.target.value, setCity)}
                />
                {errors.city && <span className="off-form__error-text">{errors.city}</span>}
              </div>

              <div className="off-form__group off-form__group--third">
                <label className="off-form__label">Province / State</label>
                <input
                  type="text"
                  className="off-form__input"
                  placeholder="e.g. AB, CA"
                  value={province}
                  onChange={(e) => setProvince(e.target.value)}
                />
              </div>

              <div className="off-form__group off-form__group--third">
                <label className="off-form__label">Zip / Postal Code {inPersonType === 'provider' && <span className="off-form__required">*</span>}</label>
                <input
                  type="text"
                  className={`off-form__input ${errors.zipcode ? 'off-form__input--error' : ''}`}
                  placeholder="e.g. T2P 2M1"
                  value={zipcode}
                  onChange={(e) => handleFieldChange('zipcode', e.target.value, setZipcode)}
                />
                {errors.zipcode && <span className="off-form__error-text">{errors.zipcode}</span>}
              </div>

              <div className="off-form__group off-form__group--third">
                <label className="off-form__label">Country</label>
                <input
                  type="text"
                  className="off-form__input"
                  placeholder="e.g. Canada"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SECTION 4: MEDIA */}
      <div className="off-form__section">
        <h2 className="off-form__section-title">Section 4 — Media</h2>

        <div className="off-form__group">
          <label className="off-form__label">Cover Image</label>
          <div className="off-form__image-upload-wrapper">
            {coverImageUrl ? (
              <div className="off-form__image-preview-box">
                <img src={coverImageUrl} alt="Cover Preview" className="off-form__image-preview" />
                <button type="button" className="off-form__image-clear-btn" onClick={() => setCoverImageUrl('')}>Remove</button>
              </div>
            ) : (
              <label className="off-form__upload-btn">
                {isUploadingCover ? "Uploading Cover..." : "Upload Cover Image (Max 5MB)"}
                <input
                  type="file"
                  accept="image/png, image/jpeg"
                  onChange={handleCoverUpload}
                  disabled={isUploadingCover}
                  style={{ display: 'none' }}
                />
              </label>
            )}
          </div>
        </div>

        <div className="off-form__group">
          <label className="off-form__label">Gallery Images (Up to 5)</label>
          <div className="off-form__gallery-list">
            {galleryImageUrls.map((url, idx) => (
              <div key={idx} className="off-form__gallery-item">
                <img src={url} alt={`Gallery ${idx + 1}`} className="off-form__gallery-thumb" />
                <button type="button" className="off-form__gallery-remove" onClick={() => removeGalleryImage(idx)}>&times;</button>
              </div>
            ))}

            {galleryImageUrls.length < 5 && (
              <label className="off-form__gallery-add-btn">
                {isUploadingGallery ? "Uploading..." : "+ Add Image"}
                <input
                  type="file"
                  multiple
                  accept="image/png, image/jpeg"
                  onChange={handleGalleryUpload}
                  disabled={isUploadingGallery}
                  style={{ display: 'none' }}
                />
              </label>
            )}
          </div>
        </div>
      </div>

      {/* SECTION 5: AVAILABILITY */}
      {category !== 'event' && category !== 'product' && (
        <div className="off-form__section">
          <h2 className="off-form__section-title">Section 5 — Availability</h2>

          <div className="off-form__row">
            <div className="off-form__group off-form__group--half">
              <label className="off-form__label">Available From</label>
              <AdventureDateTimePicker
                type="date"
                label="Available From"
                value={availableFrom}
                onChange={setAvailableFrom}
                minDate={getTodayFormatted()}
              />
            </div>

            <div className="off-form__group off-form__group--half">
              <label className="off-form__label">Available Until</label>
              <AdventureDateTimePicker
                type="date"
                label="Available Until"
                value={availableUntil}
                onChange={setAvailableUntil}
                minDate={availableFrom || getTodayFormatted()}
              />
            </div>
          </div>

          <div className="off-form__group">
            <label className="off-form__label">Notes on Availability</label>
            <textarea
              className="off-form__input off-form__input--textarea-small"
              placeholder="e.g. Available weekdays only, afternoons preferred"
              value={availabilityNotes}
              onChange={(e) => setAvailabilityNotes(e.target.value)}
              maxLength={500}
            />
          </div>
        </div>
      )}

      {/* SECTION 5/6: STATUS */}
      <div className="off-form__section">
        <h2 className="off-form__section-title">
          {category === 'event' || category === 'product' ? 'Section 5 — Status' : 'Section 6 — Status'}
        </h2>
        <div className="off-form__group">
          <div className="off-form__radio-group">
            <label className="off-form__radio-label">
              <input
                type="radio"
                name="status"
                value="active"
                checked={status === 'active'}
                onChange={() => setStatus('active')}
              />
              Save as Active (Visible immediately in public listings)
            </label>
            <label className="off-form__radio-label">
              <input
                type="radio"
                name="status"
                value="draft"
                checked={status === 'draft'}
                onChange={() => setStatus('draft')}
              />
              Save as Draft (Private, not listed to customers)
            </label>
          </div>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="off-form__actions">
        <button type="button" className="off-form__btn off-form__btn--cancel" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </button>
        <button 
          type="submit" 
          className="off-form__btn off-form__btn--submit" 
          disabled={isSubmitting}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
        >
          {isSubmitting ? (
            <>
              <span style={{
                width: '14px',
                height: '14px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderTopColor: '#fff',
                borderRadius: '50%',
                animation: 'spinner-spin 0.8s linear infinite',
                display: 'inline-block',
              }}></span>
              {isEdit ? "Saving..." : "Publishing..."}
            </>
          ) : (
            isEdit ? "Save Changes" : "Publish Offering"
          )}
        </button>
      </div>
    </form>
  );
}
